/**
 * VOTE — die Abstimmung als mitlaufender CTA (Jans Skizze 27.09.2026):
 * oben die Frage, darunter 2–6 Antworten zum Antippen.
 *
 *   EINE Antwort   → Antippen gibt die Stimme sofort ab (ein Tipp am Handy).
 *   MEHRERE        → ankreuzen, dann „Abstimmen".
 *
 * Danach steht der Danke-Satz da, die gewählten Antworten bleiben markiert.
 *
 * STUFE 1 (ohne Backend): Die Stimme wird noch NICHT gezählt. Im Leser merkt
 * sich das Gerät nur, dass schon abgestimmt wurde (localStorage), damit die
 * Frage beim Wiederkommen nicht erneut erscheint. Zählung + Ergebnisse folgen
 * nach Absprache mit Benedikt (Stufe 2).
 *
 * Im EDITOR ist die Abstimmung bedienbar, damit man beide Zustände sieht; nichts
 * wird gemerkt, und ↺ setzt sie zurück.
 */
import { useEffect, useState } from "react";
import type { CSSProperties, FunctionComponent } from "react";
import type { Color, CtaAbgabe, ResolvedRunningCta, ResolvedVote } from "@scrolly/post-types";
// Gewählter Schnitt = Grundtext (Antworten); Frage, gewählte Antwort und Danke
// bleiben kräftiger, nie dünner (Jan 07.10.2026, s. schrift-staerke.ts)
import { betonterSchnitt, grundSchnitt } from "@scrolly/post-types";
import { ctaGeraeteId, leseGemerkt, merke, ZUKLAPPEN_NACH_MS } from "./ctaMerken";
import DankeZeile from "./DankeZeile";

const farbe = (c: Color, alpha = 1): string => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a * alpha})`;

interface Props {
    strip: ResolvedRunningCta;
    vote: ResolvedVote;
    editorMode?: boolean;
    /** Gesetzt = echter Leser: Abgabe merken und melden. Fehlt = Vorschau. */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
}

const RunningVote: FunctionComponent<Props> = ({ strip, vote, editorMode, onCtaAntwort }) => {
    // Nur echte Leser merken sich die Abgabe (Vorschauen bleiben frisch)
    const echterLeser = !editorMode && Boolean(onCtaAntwort);
    const [wahl, setWahl] = useState<string[]>([]);
    const [abgegeben, setAbgegeben] = useState(false);
    // Nach der Abgabe kurz offen, dann zur schmalen Danke-Zeile (ZUKLAPPEN_NACH_MS)
    const [zu, setZu] = useState(false);
    useEffect(() => {
        if (!abgegeben || zu) return undefined;
        const t = window.setTimeout(() => setZu(true), ZUKLAPPEN_NACH_MS);
        return () => window.clearTimeout(t);
    }, [abgegeben, zu]);

    // Leser: schon abgestimmt? Dann gleich den Danke-Zustand zeigen.
    useEffect(() => {
        if (!echterLeser) return;
        const gemerkt = leseGemerkt(strip.id);
        if (gemerkt) {
            // Wiederkommen: gleich die schmale Zeile
            setWahl(gemerkt);
            setAbgegeben(true);
            setZu(true);
        }
    }, [echterLeser, strip.id]);

    const abgeben = (auswahl: string[]) => {
        if (auswahl.length === 0) return;
        setWahl(auswahl);
        setAbgegeben(true);
        if (echterLeser && onCtaAntwort) {
            merke(strip.id, auswahl);
            onCtaAntwort({ ctaId: strip.id, art: "vote", antwort: auswahl, geraet: ctaGeraeteId() });
        }
    };

    const tippe = (id: string) => {
        if (abgegeben) return;
        if (!vote.mehrfach) {
            abgeben([id]);
            return;
        }
        setWahl((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));
    };

    const text = strip.textColor;
    const schrift: CSSProperties = strip.fontFamily
        ? { fontFamily: `'${strip.fontFamily}', sans-serif` }
        : {};

    const k = vote.knopf;
    const kontur = k.borderColor ? farbe(k.borderColor) : farbe(text);
    const knopfBereit = wahl.length > 0;
    const danke = vote.danke || "Danke fürs Mitmachen!";
    const zuruecksetzen = () => {
        setWahl([]);
        setAbgegeben(false);
        setZu(false);
    };

    if (zu) {
        return (
            <div style={{ width: "100%", color: farbe(text), fontSize: `${strip.fontSize}px`, ...schrift }}>
                <DankeZeile
                    text={danke}
                    staerke={betonterSchnitt(strip.fontWeight, 600)}
                    knopfGrund={farbe(text, 0.18)}
                    onZurueck={editorMode ? zuruecksetzen : undefined}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "560px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                color: farbe(text),
                fontSize: `${strip.fontSize}px`,
                lineHeight: 1.3,
                ...schrift,
            }}
        >
            {vote.frage ? (
                <div style={{ fontWeight: betonterSchnitt(strip.fontWeight, 700), paddingRight: "28px" }}>{vote.frage}</div>
            ) : null}

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {vote.optionen.map((o) => {
                    const an = wahl.includes(o.id);
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => tippe(o.id)}
                            disabled={abgegeben}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                width: "100%",
                                minHeight: "40px",
                                padding: "6px 12px",
                                textAlign: "left",
                                font: "inherit",
                                fontWeight: an ? betonterSchnitt(strip.fontWeight, 700) : grundSchnitt(strip.fontWeight, 500),
                                color: "inherit",
                                background: an ? farbe(text, 0.22) : farbe(text, 0.08),
                                border: `1.5px solid ${farbe(text, an ? 0.9 : 0.45)}`,
                                borderRadius: "8px",
                                cursor: abgegeben ? "default" : "pointer",
                                opacity: abgegeben && !an ? 0.55 : 1,
                            }}
                        >
                            {/* Kreis = eine Antwort, Kästchen = mehrere */}
                            <span
                                aria-hidden
                                style={{
                                    flexShrink: 0,
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: vote.mehrfach ? "3px" : "50%",
                                    border: `2px solid ${farbe(text)}`,
                                    background: an ? farbe(text) : "transparent",
                                    boxShadow: an ? `inset 0 0 0 2px ${farbe(strip.fillColor)}` : undefined,
                                }}
                            />
                            <span>{o.text}</span>
                        </button>
                    );
                })}
            </div>

            {abgegeben ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: betonterSchnitt(strip.fontWeight, 600) }}>
                    <span>{danke}</span>
                    {editorMode ? (
                        <button
                            type="button"
                            title="Abstimmung zurücksetzen (nur im Editor)"
                            onClick={zuruecksetzen}
                            style={{
                                border: "none",
                                background: farbe(text, 0.18),
                                color: "inherit",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                cursor: "pointer",
                                padding: 0,
                            }}
                        >
                            ↺
                        </button>
                    ) : null}
                </div>
            ) : vote.mehrfach ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                        type="button"
                        onClick={() => abgeben(wahl)}
                        disabled={!knopfBereit}
                        style={{
                            color: kontur,
                            border: `${k.borderWidth}px solid ${kontur}`,
                            borderRadius: `${k.radius}px`,
                            background: k.fillColor ? farbe(k.fillColor) : "transparent",
                            padding: "0.5em 1.4em",
                            fontWeight: k.fontWeight,
                            fontSize: "inherit",
                            cursor: knopfBereit ? "pointer" : "default",
                            opacity: knopfBereit ? 1 : 0.5,
                            ...(k.fontFamily ? { fontFamily: `'${k.fontFamily}', sans-serif` } : {}),
                        }}
                    >
                        {k.text}
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default RunningVote;
